import { isValidIp, validationResult } from "../../../../utils/validation";

const MAX_LEN = 256;

/** FQDN only — no wildcards (Add domain). */
const ADD_DOMAIN_HOSTNAME_REGEX =
  /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const LOOKS_LIKE_IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

/** Multi-letter gTLDs only — any 2-letter alphabetic label is treated as a ccTLD. */
const MULTI_LETTER_GTLDS = new Set([
  "aero",
  "app",
  "asia",
  "biz",
  "blog",
  "bond",
  "cat",
  "cfd",
  "click",
  "cloud",
  "club",
  "com",
  "coop",
  "dev",
  "digital",
  "edu",
  "gov",
  "icu",
  "info",
  "int",
  "jobs",
  "life",
  "link",
  "live",
  "lol",
  "ltd",
  "mil",
  "mobi",
  "museum",
  "name",
  "net",
  "news",
  "online",
  "org",
  "page",
  "pro",
  "sbs",
  "shop",
  "site",
  "store",
  "tech",
  "tel",
  "today",
  "top",
  "travel",
  "vip",
  "work",
  "world",
  "xyz",
]);

function rejectShape(label, value) {
  const v = String(value ?? "").trim();
  if (/\s/.test(v)) return validationResult(false, `${label} must not contain spaces`);
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(v)) {
    return validationResult(false, `Enter ${label.toLowerCase()} without a URL scheme`);
  }
  if (v.includes("@")) {
    return validationResult(false, `Enter ${label.toLowerCase()} without user info`);
  }
  if (/[/?#]/.test(v)) {
    return validationResult(false, `Enter ${label.toLowerCase()} only (no path or query)`);
  }
  if (v.includes("*")) {
    return validationResult(false, `${label} does not allow wildcards`);
  }
  if (/^\[.+\]$/.test(v)) {
    return validationResult(false, "Enter IPv6 without brackets");
  }
  if (/^\[.*\]:\d+$/.test(v) || ((v.match(/:/g) || []).length === 1 && /:\d+$/.test(v))) {
    return validationResult(false, "Do not include a port");
  }
  return null;
}

function isAllNumericLabels(value) {
  return value.split(".").every((part) => /^\d+$/.test(part));
}

function rejectInvalidIpv4Lookalike(label, value) {
  if (LOOKS_LIKE_IPV4.test(value) && !isValidIp(value)) {
    return validationResult(false, `Enter a valid ${label.toLowerCase()} (invalid IPv4)`);
  }
  return null;
}

function rejectWwwPrefix(value) {
  if (/^www\./i.test(value)) {
    return validationResult(false, "Enter the domain without www");
  }
  return null;
}

function isPlausiblePublicTld(tld) {
  const t = String(tld || "").toLowerCase();
  if (/^[a-z]{2}$/.test(t)) return true;
  if (/^xn--[a-z0-9-]+$/i.test(t)) return true;
  return MULTI_LETTER_GTLDS.has(t);
}

function rejectImplausibleTld(value) {
  const labels = value.split(".");
  const tld = labels[labels.length - 1];
  if (!isPlausiblePublicTld(tld)) {
    return validationResult(false, "Enter a valid domain with a recognized TLD (e.g. example.com)");
  }
  return null;
}

/** Add-domain only — does not change shared settings validators. */
export function validateAddDomainWebsite(value) {
  const v = String(value ?? "").trim();
  if (!v) return validationResult(false, "Website is required");
  if (v.length > MAX_LEN) {
    return validationResult(false, `Website must be ${MAX_LEN} characters or fewer`);
  }
  const shapeError = rejectShape("Website", v);
  if (shapeError) return shapeError;
  const wwwError = rejectWwwPrefix(v);
  if (wwwError) return wwwError;
  if (isValidIp(v) || LOOKS_LIKE_IPV4.test(v) || isAllNumericLabels(v)) {
    return validationResult(false, "Enter a domain name, not an IP address");
  }
  if (!ADD_DOMAIN_HOSTNAME_REGEX.test(v)) {
    return validationResult(false, "Enter a valid domain (e.g. example.com)");
  }
  const tldError = rejectImplausibleTld(v);
  if (tldError) return tldError;
  return validationResult(true);
}

/** Add-domain only — does not change shared settings validators. */
export function validateAddDomainOrigin(value) {
  const v = String(value ?? "").trim();
  if (!v) return validationResult(false, "Origin is required");
  if (v.length > MAX_LEN) {
    return validationResult(false, `Origin must be ${MAX_LEN} characters or fewer`);
  }
  const shapeError = rejectShape("Origin", v);
  if (shapeError) return shapeError;
  const ipv4Error = rejectInvalidIpv4Lookalike("Origin", v);
  if (ipv4Error) return ipv4Error;
  if (isValidIp(v)) return validationResult(true);
  if (isAllNumericLabels(v)) {
    return validationResult(false, "Enter a valid IP or hostname (e.g. 203.0.113.10 or origin.example.com)");
  }
  if (!ADD_DOMAIN_HOSTNAME_REGEX.test(v)) {
    return validationResult(
      false,
      "Enter a valid IP or hostname (e.g. 203.0.113.10 or origin.example.com)"
    );
  }
  return validationResult(true);
}
